<template>
    <div class="transfer-description">
        <p class="transfer-description-title">
            {{ description.TransferVehicle.CategoryType }}
        </p>

        <div class="transfer-description-items">
            <div
                v-for="(item, index) in items"
                :key="index"
                class="transfer-description-item">
                <Icon :type="item.icon" color="grey" />
                <span>{{ item.label }}</span>
            </div>
        </div>

        <div class="transfer-description-logos">
            <LazyImage
                :src="cleanTransferImage(description.TransferVehicle.Image)"
                :alt="description.TransferVehicle.CategoryType"
                class="clickable"
                :onClick="
                    () =>
                        setActiveImage(
                            description.TransferVehicle.Image,
                            description.TransferVehicle.CategoryType
                        )
                " />
            <div class="transfer-description-logos-provider">
                <span class="provider-label">
                    {{ t({ key: 'checkout.provided_by', suffix: ':' }) }}
                </span>
                <LazyImage
                    :src="cleanTransferImage(description.TransferPartner.Logo)"
                    :alt="description.TransferPartner.Name"
                    v-tippy="description.TransferPartner.Name" />
            </div>
        </div>

        <div v-if="activeImage" class="transfer-description-image">
            <ButtonIcon
                icon="Cross"
                :ariaLabel="t({ key: 'shared.close' })"
                :onClick="resetActiveImage" />
            <div class="content">
                <LazyImage :src="activeImage.src" :alt="activeImage.alt" />
                <Notification icon="InfoCircle">
                    {{ t({ key: 'shared.transfer_vehicle_title_disclaimer' }) }}
                </Notification>
            </div>
        </div>

        <Notification v-if="freeCancellationDate" variant="positive" icon="Check" :tier="1">
            <div class="transfer-description-cancellation">
                <strong>
                    {{ t({ key: 'cancellation.free_cancellation_until' }) }}
                </strong>
                {{ freeCancellationDate }}
            </div>
        </Notification>

        <slot name="notifications" />
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType, computed, ref } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { TransferOption } from '@/api/Transfer/transfer.interfaces';
    import { TransferProduct } from '@/api/Cart/cart.interfaces';
    import { Type as IconType } from '@ui/components/Icon/Icon.interfaces';
    import { convertToDate, formatMinutes } from '@shared/helpers/datetime/datetime';
    import { LabelFormat } from '@shared/interfaces/translation';
    import { getMoneyObjectAsString } from '@shared/helpers/money';
    import { getLocaleDayShortMonthYear } from '@shared/helpers/datetime/datetime.formatters';
    import { cleanTransferImage } from '@/helpers/transfer/transfer';
    import { isNotNil } from '@shared/utils/value';
    import { logStatsigCheckoutTransferInteraction } from '@/tracking/statsig/checkout.functions';
    import { mapDescription } from './TransferDescription.helpers';
    import LazyImage from '@ui/components/LazyImage/LazyImage.vue';
    import Icon from '@ui/components/Icon/Icon.vue';
    import ButtonIcon from '@ui/components/Button/ButtonIcon.vue';
    import Notification from '@ui/components/Notification/Notification.vue';

    interface Item {
        icon: IconType;
        label: string;
    }

    interface ActiveImage {
        src: string;
        alt: string;
    }

    export default defineComponent({
        name: 'TransferDescription',
        methods: { t, cleanTransferImage, getMoneyObjectAsString },
        components: {
            LazyImage,
            Icon,
            ButtonIcon,
            Notification,
        },
        props: {
            transfer: {
                type: Object as PropType<TransferOption | TransferProduct>,
                required: true,
            },
        },
        setup(props) {
            const description = computed(() => mapDescription(props.transfer));
            const { MaxLargeLuggage, Duration, FreeCancelUntil } = description.value;

            const activeImage = ref<ActiveImage | null>(null);

            const items = computed<Item[]>(() => {
                return [
                    {
                        icon: 'Timer',
                        label: `${formatMinutes(Duration ?? 0)}`,
                    },
                    MaxLargeLuggage && {
                        icon: 'CheckedBag',
                        label: `${MaxLargeLuggage} ${t({ key: 'shared.baggage', format: LabelFormat.LowerCase })}`,
                    },
                ].filter(isNotNil) as Item[];
            });

            const freeCancellationDate = computed<string | undefined>(() => {
                if (FreeCancelUntil) {
                    const date = convertToDate(FreeCancelUntil);
                    if (date) {
                        return getLocaleDayShortMonthYear(date);
                    }
                }

                return undefined;
            });

            function setActiveImage(src: string, alt: string) {
                logStatsigCheckoutTransferInteraction('Image', props.transfer, null);
                activeImage.value = { src, alt };
            }

            function resetActiveImage() {
                activeImage.value = null;
            }

            return {
                items,
                activeImage,
                setActiveImage,
                resetActiveImage,
                freeCancellationDate,
                LabelFormat,
                description,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './TransferDescription.scss';
</style>
